/**
 * Leave Calendar page - Monthly view of who is on leave
 */

import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { leaveApi } from '../api/leaveApi'
import LoadingPage from '@/shared/components/LoadingPage'
import { Link } from 'react-router-dom'
import type { LeaveRequest, PublicHoliday } from '../types/leave.types'

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

interface CalendarDay {
  date: Date
  day: number
  isCurrentMonth: boolean
  isToday: boolean
  isWeekend: boolean
  isHoliday: boolean
  holiday?: PublicHoliday
  leaves: LeaveRequest[]
}

const LeaveCalendarPage = () => {
  const today = new Date()
  const [currentYear, setCurrentYear] = useState(today.getFullYear())
  const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1) // 1-indexed

  const { data, isLoading } = useQuery({
    queryKey: ['leaveCalendar', currentYear, currentMonth],
    queryFn: () => leaveApi.getLeaveCalendar(currentYear, currentMonth),
  })

  const { data: holidaysData, isLoading: isLoadingHolidays } = useQuery({
    queryKey: ['publicHolidays', currentYear],
    queryFn: () => leaveApi.getPublicHolidays(currentYear),
  })

  const leaves = data?.data || []
  const holidays = holidaysData?.data || []

  // Create a map of date string to holiday for quick lookup
  const holidayMap = useMemo(() => {
    const map = new Map<string, PublicHoliday>()
    holidays.forEach((holiday) => {
      if (holiday.is_active) {
        // Format: YYYY-MM-DD
        const dateStr = holiday.date.split('T')[0]
        map.set(dateStr, holiday)
      }
    })
    return map
  }, [holidays])

  // Helper function to calculate working days (excluding weekends and holidays)
  const calculateWorkingDays = (startDate: string, endDate: string): number => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    let workingDays = 0

    const current = new Date(start)
    while (current <= end) {
      const dayOfWeek = current.getDay()
      const dateKey = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`

      // Count only if not weekend and not a holiday
      if (dayOfWeek !== 0 && dayOfWeek !== 6 && !holidayMap.has(dateKey)) {
        workingDays++
      }

      current.setDate(current.getDate() + 1)
    }

    return workingDays
  }

  // Generate calendar days for the current month
  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(currentYear, currentMonth - 1, 1)
    const lastDayOfMonth = new Date(currentYear, currentMonth, 0)
    const daysInMonth = lastDayOfMonth.getDate()
    const startingDayOfWeek = firstDayOfMonth.getDay()

    const days: CalendarDay[] = []

    // Helper to format date as YYYY-MM-DD
    const formatDateKey = (d: Date) => {
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const dayNum = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${dayNum}`
    }

    // Add days from previous month to fill the first week
    const prevMonth = new Date(currentYear, currentMonth - 2, 1)
    const daysInPrevMonth = new Date(currentYear, currentMonth - 1, 0).getDate()
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i
      const date = new Date(prevMonth.getFullYear(), prevMonth.getMonth(), day)
      const dateKey = formatDateKey(date)
      const holiday = holidayMap.get(dateKey)
      days.push({
        date,
        day,
        isCurrentMonth: false,
        isToday: false,
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isHoliday: !!holiday,
        holiday,
        leaves: [],
      })
    }

    // Add days of current month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth - 1, day)
      const dateKey = formatDateKey(date)
      const holiday = holidayMap.get(dateKey)
      const isToday =
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()

      // Find leaves that include this day (excluding weekends and holidays)
      const dayLeaves = leaves.filter((leave) => {
        // Use date string comparison to avoid timezone issues
        const leaveStartDate = leave.start_date.split('T')[0]
        const leaveEndDate = leave.end_date.split('T')[0]
        // Check if date is within leave period
        if (dateKey < leaveStartDate || dateKey > leaveEndDate) return false
        // Exclude weekends (Sunday = 0, Saturday = 6)
        if (date.getDay() === 0 || date.getDay() === 6) return false
        // Exclude public holidays
        if (holiday) return false
        return true
      })

      days.push({
        date,
        day,
        isCurrentMonth: true,
        isToday,
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isHoliday: !!holiday,
        holiday,
        leaves: dayLeaves,
      })
    }

    // Add days from next month to complete the last week
    const remainingDays = 42 - days.length // 6 rows * 7 days
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(currentYear, currentMonth, day)
      const dateKey = formatDateKey(date)
      const holiday = holidayMap.get(dateKey)
      days.push({
        date,
        day,
        isCurrentMonth: false,
        isToday: false,
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isHoliday: !!holiday,
        holiday,
        leaves: [],
      })
    }

    return days
  }, [currentYear, currentMonth, leaves, holidayMap, today])

  const goToPrevMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12)
      setCurrentYear(currentYear - 1)
    } else {
      setCurrentMonth(currentMonth - 1)
    }
  }

  const goToNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1)
      setCurrentYear(currentYear + 1)
    } else {
      setCurrentMonth(currentMonth + 1)
    }
  }

  const goToToday = () => {
    setCurrentYear(today.getFullYear())
    setCurrentMonth(today.getMonth() + 1)
  }

  if (isLoading || isLoadingHolidays) return <LoadingPage />

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <Link to="/leave" className="hover:text-red-600">Leave</Link>
            <span>/</span>
            <span>Calendar</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Leave Calendar</h1>
          <p className="text-gray-600 mt-1">View who is on leave this month</p>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="flex items-center gap-2">
            <button
              onClick={goToPrevMonth}
              className="p-2 hover:bg-white rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h2 className="text-xl font-semibold text-gray-900 min-w-[200px] text-center">
              {MONTHS[currentMonth - 1]} {currentYear}
            </h2>
            <button
              onClick={goToNextMonth}
              className="p-2 hover:bg-white rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <button
            onClick={goToToday}
            className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            Today
          </button>
        </div>

        {/* Weekday Headers */}
        <div className="grid grid-cols-7 border-b border-gray-200">
          {WEEKDAYS.map((day, index) => (
            <div
              key={day}
              className={`p-3 text-center text-sm font-semibold ${
                index === 0 || index === 6 ? 'text-red-500 bg-red-50' : 'text-gray-700 bg-gray-50'
              }`}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, index) => (
            <div
              key={index}
              className={`min-h-[120px] p-2 border-b border-r border-gray-100 ${
                !day.isCurrentMonth ? 'bg-gray-50' : ''
              } ${day.isHoliday && day.isCurrentMonth ? 'bg-amber-50' : ''} ${
                day.isWeekend && day.isCurrentMonth && !day.isHoliday ? 'bg-red-50/30' : ''
              } ${day.isToday ? 'bg-red-50' : ''}`}
            >
              <div className={`text-sm font-medium mb-1 ${
                !day.isCurrentMonth ? 'text-gray-400' :
                day.isToday ? 'text-red-600' :
                day.isHoliday ? 'text-amber-700' :
                day.isWeekend ? 'text-red-500' : 'text-gray-900'
              }`}>
                {day.isToday ? (
                  <span className="inline-flex items-center justify-center w-6 h-6 bg-red-600 text-white rounded-full">
                    {day.day}
                  </span>
                ) : (
                  day.day
                )}
              </div>

              {/* Holiday banner */}
              {day.isHoliday && day.holiday && day.isCurrentMonth && (
                <div
                  className="text-xs px-2 py-1 rounded mb-1 truncate bg-amber-100 border-l-3 border-amber-500 text-amber-800 font-medium"
                  style={{ borderLeft: '3px solid #f59e0b' }}
                  title={day.holiday.name}
                >
                  {day.holiday.name}
                </div>
              )}

              {/* Leave entries */}
              <div className="space-y-1">
                {day.leaves.slice(0, day.isHoliday ? 2 : 3).map((leave) => (
                  <div
                    key={leave.id}
                    className="text-xs px-2 py-1 rounded truncate"
                    style={{
                      backgroundColor: leave.leave_type?.color ? `${leave.leave_type.color}20` : '#e5e7eb',
                      borderLeft: `3px solid ${leave.leave_type?.color || '#6b7280'}`,
                    }}
                    title={`${leave.user?.full_name} - ${leave.leave_type?.name}`}
                  >
                    <span className="font-medium">{leave.user?.full_name}</span>
                  </div>
                ))}
                {day.leaves.length > (day.isHoliday ? 2 : 3) && (
                  <div className="text-xs text-gray-500 px-2">
                    +{day.leaves.length - (day.isHoliday ? 2 : 3)} more
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 bg-white rounded-xl shadow p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Legend</h3>
        <div className="flex flex-wrap gap-4">
          {/* Public Holiday indicator */}
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-amber-500" />
            <span className="text-sm text-gray-600">Public Holiday</span>
          </div>
          {/* Leave types from the data */}
          {Array.from(new Set(leaves.map(l => l.leave_type?.id)))
            .map(id => leaves.find(l => l.leave_type?.id === id)?.leave_type)
            .filter(Boolean)
            .map((leaveType) => (
              <div key={leaveType!.id} className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: leaveType!.color || '#6b7280' }}
                />
                <span className="text-sm text-gray-600">{leaveType!.name}</span>
              </div>
            ))}
        </div>
      </div>

      {/* Public Holidays List */}
      {holidays.filter(h => h.is_active).length > 0 && (
        <div className="mt-4 bg-white rounded-xl shadow p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Public Holidays in {currentYear}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {holidays
              .filter(h => h.is_active)
              .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
              .map((holiday) => (
                <div
                  key={holiday.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-amber-100 bg-amber-50/50"
                >
                  <div className="w-2 h-10 rounded-full bg-amber-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {holiday.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(holiday.date).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </p>
                    {holiday.description && (
                      <p className="text-xs text-gray-400 truncate">
                        {holiday.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="mt-4 bg-white rounded-xl shadow p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          People on Leave in {MONTHS[currentMonth - 1]} {currentYear}
        </h3>
        {leaves.length === 0 ? (
          <p className="text-sm text-gray-500">No approved leaves for this month.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {leaves.map((leave) => (
              <div
                key={leave.id}
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50"
              >
                <div
                  className="w-2 h-10 rounded-full"
                  style={{ backgroundColor: leave.leave_type?.color || '#6b7280' }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {leave.user?.full_name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {leave.leave_type?.name} - {calculateWorkingDays(leave.start_date, leave.end_date)} working day(s)
                  </p>
                  <p className="text-xs text-gray-400">
                    {new Date(leave.start_date).toLocaleDateString()} - {new Date(leave.end_date).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default LeaveCalendarPage
